/* eslint-disable no-unused-expressions */
import React, { useCallback, useEffect, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Dropdown, Menu, Tooltip } from 'antd'
import { FormattedMessage, injectIntl, intlShape } from 'react-intl'

import useErrorHandler from 'util/useErrorHandler'
import { isPosthogEnabled } from 'contexts/Cookies/postHogConfig'
// import { Link } from 'react-router-dom'
import { useAppState } from 'services/store'
import { ME_QUERY, UPDATE_ACCOUNT_MUTATION } from 'services/user'

import { AntMenu } from 'components/Global/AntDesign'
import Icon from 'components/Global/Icon'
import MenuItem from 'components/Global/MenuItem'
import Switch from 'components/Global/Switch'
import { useMessages } from 'components/Layout/Notifications'
import { useUi } from 'components/Layout/UiProvider'
import { EditProfileModal } from 'components/Modals/Settings/EditProfileModal'

import { MultiIconMenuItem } from './styled'
import UserBadge from './UserBadge'

// TODO: Fix Heylen Dark mode
const domainsWithHeylen = [
  'smart.heylenwarehouses.com',
  'buildingtechnologies21.be',
  'buildingtechnologies21.com',
  'buildingtechnologies21.nl',
  'www.buildingtechnologies21.be',
  'www.buildingtechnologies21.com',
  'www.buildingtechnologies21.nl',
  'whitelabel.canary.expert',
]

const isHeylenDomain = domainsWithHeylen.includes(window.location.hostname)

const ProfileMenu = ({ intl }) => {
  const [editingProfile, setEditingProfile] = useState(false)

  const { success, error: errorMessage } = useMessages()

  const handleCloseProfileModal = () => {
    setEditingProfile(false)
  }

  const {
    appState: { logoutUser },
  } = useAppState()

  const { toggleMode, mode } = useUi()

  const [errorHandler] = useErrorHandler()

  const { data, error, loading, refetch } = useQuery(ME_QUERY)

  const [updateAccount] = useMutation(UPDATE_ACCOUNT_MUTATION)

  useEffect(() => {
    if (data?.me?.id && window.posthog && isPosthogEnabled) {
      try {
        window.posthog.identify(data.me.id.toString(), {
          email: data?.me?.email,
          name: `${data?.me?.firstname ?? 'firstname'} ${data?.me?.surname ?? 'surname'}`,
        })
      } catch (e) {
        window.posthog.capture('posthog_identify_error', {
          error: e,
        })
      }
    }
  }, [data?.me])

  const handleToggleDarkMode = useCallback(() => {
    toggleMode()
  }, [toggleMode])

  const handleToggleAdvanced = useCallback(
    async (advanced) => {
      try {
        await updateAccount({
          variables: {
            data: {
              id: data.me.id,
              advanced,
            },
          },
        })

        refetch()

        success(
          intl.formatMessage({
            id: advanced
              ? 'settings.updateProfile.advanced.active.success'
              : 'settings.updateProfile.advanced.disabled.success',
          })
        )
      } catch (e) {
        console.error(e)
        errorMessage(intl.formatMessage({ id: 'settings.updateProfile.advanced.failed' }))
      }
    },
    [updateAccount, intl, errorMessage, success, data, refetch]
  )

  if (error) {
    errorHandler(error)
    console.error(error)
  }

  if (!data || loading) {
    return null
  }

  const user = data.me

  const menu = (
    <AntMenu selectable={false}>
      <Menu.Item>
        <MenuItem>
          <Tooltip title={user.email} placement="left">
            <span>
              <FormattedMessage id="topBar.profileMenu.hello" values={{ name: user.firstname }} />
            </span>
          </Tooltip>
        </MenuItem>
      </Menu.Item>

      <Menu.Divider />

      <MultiIconMenuItem>
        <MenuItem>
          <Tooltip title={intl.formatMessage({ id: 'support.title' })} placement="bottom">
            <a
              href="https://brightcanary.freshdesk.com/support/home"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon="fas fa-comments-question-check" />
            </a>
          </Tooltip>
          {/* <Tooltip title={intl.formatMessage({ id: 'contact.title' })} placement="bottom">
            <Link exact to={'/user/login/contact'}>
              <Icon icon="fas fa-envelope" />
            </Link>
          </Tooltip> */}
          <Tooltip title={intl.formatMessage({ id: 'status' })} placement="bottom">
            <a href="https://status.iot.canary.expert" target="_blank" rel="noopener noreferrer">
              <Icon icon="fas fa-traffic-light" />
            </a>
          </Tooltip>
        </MenuItem>
      </MultiIconMenuItem>

      <Menu.Divider />

      {!isHeylenDomain && (
        <Menu.Item>
          <MenuItem icon="fas fa-circle-half-stroke">
            <FormattedMessage id="topBar.profileMenu.theme" />
            <Switch checked={mode} onChange={handleToggleDarkMode} />
          </MenuItem>
        </Menu.Item>
      )}

      <Menu.Item>
        <MenuItem icon="fas fa-graduation-cap">
          <FormattedMessage id="topBar.profileMenu.advanced" />
          <Switch checked={user.advanced} onChange={handleToggleAdvanced} />
        </MenuItem>
      </Menu.Item>

      <Menu.Item>
        <MenuItem onClick={() => setEditingProfile(true)} icon="fas fa-pen">
          <FormattedMessage id="topBar.profileMenu.editProfile" />
        </MenuItem>
      </Menu.Item>

      <Menu.Item>
        <MenuItem href={null} onClick={() => logoutUser()} icon="fas fa-power-off">
          <FormattedMessage id="topBar.profileMenu.logout" />
        </MenuItem>
      </Menu.Item>
    </AntMenu>
  )

  const initials = user ? `${user.firstname[0]}${user.surname[0]}` : ''

  return (
    <Dropdown
      overlay={
        editingProfile ? (
          <EditProfileModal userId={data.me.id} handleClose={handleCloseProfileModal} intl={intl} />
        ) : (
          menu
        )
      }
      trigger={['click']}
    >
      <div>
        <UserBadge initials={initials} avatar={data.me.avatar} />
      </div>
    </Dropdown>
  )
}

ProfileMenu.propTypes = {
  intl: intlShape.isRequired,
}
export default injectIntl(ProfileMenu)
